import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';

export interface IconOption {
  id: number | string;
  text: string;      
  keywords?: string; 
}

@Component({
  selector: 'app-icon-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './icon-picker.component.html',
  styleUrls: ['./icon-picker.component.css']
})
export class IconPickerComponent implements OnInit, OnDestroy {
  @Input() value: string = '';
  @Output() valueChange = new EventEmitter<string>();

  @Input() placeholder = 'Buscar por nombre o pega una clase FA…';
  @Input() pageSize = 24;

  @Input() remoteSearch?: (q: string, page: number, pageSize: number) => Promise<IconOption[]>;
  @Input() options: IconOption[] = [];

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  open = false;
  query = '';
  results: IconOption[] = [];
  page = 1;
  hasMore = false;
  loading = false;

  recent: string[] = [];

  activeIndex = -1;

  private search$ = new Subject<string>();
  private sub?: Subscription;

  ngOnInit(): void {
    this.sub = this.search$
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(q => { this.page = 1; this.fetch(q, true); });

    this.fetch('', true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get showUseButton(): boolean {
    const t = (this.query || '').trim();
    return t.length > 4 && (t.startsWith('fa') || t.includes(' fa-'));
  }

  labelFromClass(cls: string): string {
    const second = (cls || '').split(' ').find(x => x.startsWith('fa-')) || '';
    return second.replace(/^fa-/, '').replace(/-/g, ' ');
  }

  toggle() {
    this.open = !this.open;
    this.activeIndex = -1;
    if (this.open) {
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
      if (!this.results.length) this.fetch('', true);
    }
  }

  onQueryChange(v: string) {
    this.query = v;
    this.search$.next(v);
    this.activeIndex = -1;
  }

  async fetch(q: string, reset: boolean) {
    this.loading = true;

    let rows: IconOption[] = [];
    if (this.remoteSearch) {
      rows = await this.remoteSearch(q, this.page, this.pageSize);
      this.hasMore = rows.length === this.pageSize;
    } else {
      const norm = (s: string) => (s || '').toLowerCase();
      const pool = this.options || [];
      const filtered = pool.filter(o =>
        norm(o.text).includes(norm(q)) ||
        norm(o.keywords || '').includes(norm(q))
      );
      const start = (this.page - 1) * this.pageSize;
      rows = filtered.slice(start, start + this.pageSize);
      this.hasMore = start + this.pageSize < filtered.length;
    }

    this.results = reset ? rows : [...this.results, ...rows];
    this.loading = false;
  }

  loadMore() {
    if (this.loading || !this.hasMore) return;
    this.page += 1;
    this.fetch(this.query, false);
  }

  pick(cls: string) {
    this.value = cls;
    this.valueChange.emit(cls);
    this.pushRecent(cls);
    this.open = false;
  }

  pasteManual() {
    const t = (this.query || '').trim();
    if (t) this.pick(t);
  }

  pushRecent(cls: string) {
    const arr = this.recent.filter(x => x !== cls);
    arr.unshift(cls);
    this.recent = arr.slice(0, 8);
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    const target = ev.target as HTMLElement;
    if (!target.closest('.ipicker')) this.open = false;
  }

  onKeyDown(e: KeyboardEvent) {
    if (!this.open) return;
    if (e.key === 'Escape') { this.open = false; return; }
    if (!this.results.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
      this.scrollIntoView();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
      this.scrollIntoView();
    } else if (e.key === 'Enter' && this.activeIndex >= 0) {
      e.preventDefault();
      this.pick(this.results[this.activeIndex].text);
    }
  }

  private scrollIntoView() {
    const grid = document.querySelector('.ip-grid') as HTMLElement | null;
    if (!grid) return;
    const cell = grid.querySelectorAll('.cell')[this.activeIndex] as HTMLElement | undefined;
    if (cell) cell.scrollIntoView({ block: 'nearest' });
  }
}